import { clsx } from 'clsx';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';

type CardTone = 'default' | 'raised' | 'subtle' | 'glow';
type CardPadding = 'sm' | 'md' | 'lg';

type CardProps<T extends ElementType = 'div'> = {
  as?: T;
  tone?: CardTone;
  padding?: CardPadding;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, 'children'>;

const TONE_CLASSES: Record<CardTone, string> = {
  default: 'border border-gold/15 bg-neutral-800/60',
  raised: 'border border-gold/25 bg-night/70 shadow-md',
  subtle: 'border border-gold/10 bg-night/40',
  glow: 'border border-gold/40 bg-night/60 shadow-goldSoft',
};

const PADDING_CLASSES: Record<CardPadding, string> = {
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function Card<T extends ElementType = 'div'>({
  as,
  tone = 'default',
  padding = 'md',
  className,
  children,
  ...rest
}: CardProps<T>) {
  const Component = (as ?? 'div') as ElementType;
  return (
    <Component
      className={clsx(
        'rounded-lg backdrop-blur-sm',
        TONE_CLASSES[tone],
        PADDING_CLASSES[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
}
